import { Component } from '@theme/component';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A header search input that opens the search modal as the user types,
 * and syncs the typed value into the modal input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The header search input.
 * @extends Component<Refs>
 */
class SearchInputTriggerComponent extends Component {
  requiredRefs = ['searchInput'];

  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const dialog = this.#dialog;
    if (dialog) {
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, {
        signal: this.#controller.signal,
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  get #dialog() {
    const dialog = document.getElementById('search-modal');
    return dialog instanceof HTMLElement ? dialog : null;
  }

  get #modalInput() {
    const modalInput = document.getElementById('cmdk-input');
    return modalInput instanceof HTMLInputElement ? modalInput : null;
  }

  #handleDialogClose = () => {
    this.refs.searchInput.value = '';
  };

  openSearchModal = (event) => {
    const value = this.refs.searchInput.value.trim();

    if (!value.length) {
      return;
    }

    const dialog = this.#dialog;
    const modalInput = this.#modalInput;

    if (!dialog || !modalInput) {
      return;
    }

    if (typeof dialog.showDialog === 'function') {
      dialog.showDialog();
    }

    modalInput.value = value;

    modalInput.dispatchEvent(
      new InputEvent('input', {
        bubbles: true,
        inputType: event?.inputType || 'insertText',
      })
    );

    modalInput.focus();
  };
}

if (!customElements.get('search-input-trigger')) {
  customElements.define('search-input-trigger', SearchInputTriggerComponent);
}
